import { createFileRoute, Link } from "@tanstack/react-router";
import { useAuth } from "@/hooks/use-auth";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { Shield, ShieldCheck, ShieldAlert, Lock, Eye, Database, Server, LogIn, CheckCircle2, Clock, AlertTriangle } from "lucide-react";

export const Route = createFileRoute("/security")({
  head: () => ({
    meta: [
      { title: "Sécurité — angle-shop.com" },
      { name: "description", content: "État de la sécurité et historique des correctifs de angle-shop.com." },
    ],
  }),
  component: SecurityPage,
});

interface SecurityMeasure {
  icon: React.ReactNode;
  title: string;
  description: string;
  status: "active" | "monitoring" | "warning";
}

interface SecurityFix {
  date: string;
  title: string;
  description: string;
  severity: "critical" | "high" | "medium" | "low";
}

const securityMeasures: SecurityMeasure[] = [
  {
    icon: <Lock className="w-5 h-5" />,
    title: "Chiffrement SSL/TLS",
    description: "Toutes les communications sont chiffrées via HTTPS avec certificat SSL valide.",
    status: "active",
  },
  {
    icon: <Database className="w-5 h-5" />,
    title: "Row Level Security (RLS)",
    description: "Politiques de sécurité au niveau des lignes actives sur toutes les tables sensibles.",
    status: "active",
  },
  {
    icon: <Eye className="w-5 h-5" />,
    title: "Authentification sécurisée",
    description: "Authentification via email/mot de passe et Google OAuth avec vérification email.",
    status: "active",
  },
  {
    icon: <Server className="w-5 h-5" />,
    title: "Protection CORS",
    description: "Endpoints API protégés avec des règles CORS strictes.",
    status: "active",
  },
  {
    icon: <Shield className="w-5 h-5" />,
    title: "Validation des entrées",
    description: "Toutes les entrées utilisateur sont validées côté serveur avant traitement.",
    status: "active",
  },
  {
    icon: <ShieldCheck className="w-5 h-5" />,
    title: "Vérification JWT",
    description: "Tous les endpoints authentifiés vérifient les tokens JWT.",
    status: "active",
  },
];

const securityFixes: SecurityFix[] = [
  {
    date: "2026-05-02",
    title: "Suppression d'un endpoint exposé (get-stripe-price)",
    description: "Une Edge Function sans authentification JWT et avec une politique CORS ouverte (*) a été identifiée lors d'un scan de sécurité. Elle exposait un endpoint public capable de retourner des informations tarifaires Stripe sans aucune vérification d'identité. L'analyse du code client a confirmé qu'aucun composant ne l'utilisait — elle a donc été supprimée (code + configuration TOML + déploiement live).",
    severity: "critical",
  },
  {
    date: "2026-05-02",
    title: "Correction du contraste des couleurs (WCAG AA)",
    description: "Le ratio de contraste des noms de marques dans la section « Ils nous font confiance » était de 3.42:1, en dessous du minimum WCAG AA de 4.5:1. L'opacité CSS sur la classe text-muted-foreground/70 a été retirée, portant le ratio à ~4.9:1. Aucun changement visuel majeur — les textes restent gris stylisés mais désormais lisibles pour les utilisateurs malvoyants.",
    severity: "medium",
  },
  {
    date: "2026-05-02",
    title: "Optimisation du chargement des ressources critiques",
    description: "Ajout de balises <link rel=\"preload\"> pour les polices DM Sans et Space Grotesk (format woff2) dans le head du document afin de réduire la chaîne de requêtes critiques et améliorer les métriques FCP/LCP. Les polices sont désormais chargées en parallèle avec le CSS au lieu d'attendre la fin du parsing des feuilles de style.",
    severity: "low",
  },
];

const statusConfig = {
  active: { label: "Actif", color: "bg-emerald-100 text-emerald-800", icon: <CheckCircle2 className="w-4 h-4" /> },
  monitoring: { label: "Surveillance", color: "bg-blue-100 text-blue-800", icon: <Clock className="w-4 h-4" /> },
  warning: { label: "Attention", color: "bg-yellow-100 text-yellow-800", icon: <AlertTriangle className="w-4 h-4" /> },
};

const severityConfig = {
  critical: { label: "Critique", color: "bg-red-100 text-red-800" },
  high: { label: "Élevé", color: "bg-orange-100 text-orange-800" },
  medium: { label: "Moyen", color: "bg-yellow-100 text-yellow-800" },
  low: { label: "Faible", color: "bg-blue-100 text-blue-800" },
};

function SecurityPage() {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <main className="min-h-screen bg-background">
        <Header />
        <div className="max-w-4xl mx-auto px-4 py-20 flex justify-center">
          <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
        </div>
      </main>
    );
  }

  if (!user) {
    return (
      <main className="min-h-screen bg-background">
        <Header />
        <div className="max-w-md mx-auto px-4 py-20 text-center">
          <LogIn className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
          <h2 className="text-2xl font-bold text-foreground mb-2">Accès restreint</h2>
          <p className="text-muted-foreground mb-6">
            Cette page est réservée aux administrateurs.
          </p>
          <Link
            to="/login"
            className="inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-gradient-electric text-electric-foreground font-medium hover:opacity-90 transition-opacity"
          >
            Se connecter
          </Link>
        </div>
        <Footer />
      </main>
    );
  }

  return (
    <main className="min-h-screen bg-background">
      <Header />
      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-12">
        <div className="flex items-center gap-3 mb-8">
          <div className="p-2.5 rounded-xl bg-emerald-100">
            <ShieldCheck className="w-6 h-6 text-emerald-700" />
          </div>
          <div>
            <h1 className="text-3xl font-bold text-foreground">Sécurité</h1>
            <p className="text-muted-foreground text-sm">État des protections et historique des correctifs</p>
          </div>
        </div>

        {/* Score global */}
        <div className="border border-border rounded-2xl p-6 bg-surface-elevated mb-8">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-foreground">Score de sécurité</h2>
            <span className="text-3xl font-bold text-emerald-600">A+</span>
          </div>
          <div className="w-full bg-muted rounded-full h-2.5">
            <div className="bg-emerald-500 h-2.5 rounded-full" style={{ width: "95%" }} />
          </div>
          <p className="text-xs text-muted-foreground mt-2">
            {securityMeasures.filter(m => m.status === "active").length}/{securityMeasures.length} mesures de sécurité actives
          </p>
        </div>

        {/* Mesures de sécurité */}
        <h2 className="text-xl font-semibold text-foreground mb-4">Mesures actives</h2>
        <div className="grid gap-3 sm:grid-cols-2 mb-10">
          {securityMeasures.map((measure, i) => {
            const status = statusConfig[measure.status];
            return (
              <div
                key={i}
                className="border border-border rounded-2xl p-4 bg-surface-elevated hover:shadow-md transition-shadow"
              >
                <div className="flex items-start justify-between gap-3 mb-2">
                  <div className="flex items-center gap-2.5">
                    <div className="p-1.5 rounded-lg bg-muted">{measure.icon}</div>
                    <h3 className="font-medium text-foreground text-sm">{measure.title}</h3>
                  </div>
                  <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${status.color}`}>
                    {status.icon}
                    {status.label}
                  </span>
                </div>
                <p className="text-xs text-muted-foreground leading-relaxed">{measure.description}</p>
              </div>
            );
          })}
        </div>

        {/* Historique des correctifs */}
        <h2 className="text-xl font-semibold text-foreground mb-4">Historique des correctifs</h2>
        <div className="space-y-3">
          {securityFixes.map((fix, i) => {
            const severity = severityConfig[fix.severity];
            const date = new Date(fix.date).toLocaleDateString("fr-FR", {
              day: "numeric",
              month: "long",
              year: "numeric",
            });
            return (
              <div
                key={i}
                className="border border-border rounded-2xl p-4 bg-surface-elevated"
              >
                <div className="flex items-start justify-between gap-3 mb-1.5">
                  <div>
                    <h3 className="font-medium text-foreground text-sm">{fix.title}</h3>
                    <p className="text-xs text-muted-foreground mt-0.5">{date}</p>
                  </div>
                  <span className={`px-2.5 py-0.5 rounded-full text-xs font-medium ${severity.color}`}>
                    {severity.label}
                  </span>
                </div>
                <p className="text-xs text-muted-foreground leading-relaxed">{fix.description}</p>
              </div>
            );
          })}
        </div>
      </div>
      <Footer />
    </main>
  );
}
